import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import './scss/PaymentNotice.scss';
import { config } from '../../config';
import { fetchMembers } from '../../redux/Slices/memberSlice';

/**
 * PaymentNotice component displays notifications for billing-related actions
 * - For workspace admins: Shows notice to pay for pending seats
 * - For view-only members: Shows notice that admin needs to pay for their seat
 */
const PaymentNotice = forwardRef(({ workspaceId }, ref) => {

  const [billingStatus, setBillingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  const user = useSelector(state => state.user.user);
  const workspaces = useSelector(state => state.workspaces.workspaces);

  const { workspaceMembers } = useSelector(state => state.member);

  const dispatch = useDispatch();


  // Get the current workspace
  const workspace = workspaces.find(w => w.shortId === workspaceId);

  // Check if user is admin
  const isAdmin = workspaceMembers?.some(
    member => member._id === user?.user?._id && (member.role === 'admin')
  );




  useEffect(() => {
    const fetchBillingStatus = async () => {
      try {
        setIsLoading(true);

        const res = await fetch(config.API_URI + `/v1/billing/workspace/${workspaceId}/pending-seats`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });


        if (res.ok) {
          const data = await res.json();

          setBillingStatus(data);
        } else if (res.status !== 403) {
          // 403 is expected for non-admins (pending-seats is admin-only) — don't
          // log it as an error.
          console.error('PaymentNotice: Failed to fetch billing status:', res.status, res.statusText);
        }
      } catch (error) {
        console.error('Error fetching billing status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we should show the notice based on localStorage
    const checkDismissalStatus = () => {
      const dismissedTime = localStorage.getItem(`payment-notice-dismissed-${workspaceId}`);
      if (dismissedTime) {
        const dismissedAt = parseInt(dismissedTime);
        const now = Date.now();
        // If it's been less than 24 hours since dismissal, don't show
        if (now - dismissedAt < 24 * 60 * 60 * 1000) {
          setIsDismissed(true);
        }
      }
    };

    if (workspaceId) {
      checkDismissalStatus();
      // pending-seats is an admin-only endpoint (requireWorkspaceAdmin). Only
      // admins can fetch it; calling it as a non-admin just produced a 403. Gate
      // on isAdmin (re-runs once workspaceMembers resolves).
      if (isAdmin) {
        fetchBillingStatus();
      } else {
        setIsLoading(false);
      }
    }

  }, [workspaceId, isAdmin]);

  useEffect(() => {
    if (workspaceMembers.length < 1) {
      dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
    }
  }, [workspaceId]);


  const handleDismiss = () => {
    setIsDismissed(true);
    // Store dismissal in localStorage to prevent showing again for some time
    localStorage.setItem(`payment-notice-dismissed-${workspaceId}`, Date.now().toString());
  };


  // Don't show payment notice if workspace is on trial
  const isTrialing = billingStatus?.status === 'trialing';

  return (
    <div className="payment-notice-container" ref={ref}>
      {workspace?.isOverFreeLimit ? (
        // Free workspace is over its collaborator cap → the whole workspace is
        // view-only until they remove collaborators or upgrade (Trello behaviour).
        <div className={`payment-notice ${isAdmin ? 'admin-notice' : 'member-notice'}`}>
          <div className="notice-content">
            <div className="notice-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
                <path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" fill="#ff9800" />
              </svg>
            </div>
            <div className="notice-text">
              <p>
                <strong>This workspace is view-only.</strong> The free plan allows up to 15 collaborators.
                {isAdmin
                  ? ' Remove some collaborators or upgrade to restore editing.'
                  : ' Ask a workspace admin to remove collaborators or upgrade.'}
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="notice-actions">
              <Link to={`/w/${workspaceId}/billing`} className="pay-button">Upgrade</Link>
            </div>
          )}
        </div>
      ) : isAdmin && billingStatus?.pendingSeats > 0 && !isTrialing ? (
        <div className="payment-notice admin-notice">
          <div className="notice-content">
            <div className="notice-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
                <path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" fill="#ff9800" />
              </svg>
            </div>
            <div className="notice-text">
              <p>
                <strong>Action Required:</strong> You have {billingStatus.pendingSeats} pending seat{billingStatus.pendingSeats > 1 ? 's' : ''} that require payment.
                Members with pending seats currently have view-only access.
              </p>
            </div>
          </div>
          <div className="notice-actions">
            <Link to={`/w/${workspaceId}/billing`} className="pay-button">
              Pay Now
            </Link>
            {/* <button className="dismiss-button" onClick={handleDismiss}>
              Dismiss
            </button> */}
          </div>
        </div>
      ) : (!isAdmin && billingStatus?.pendingSeats > 0 && !isTrialing) ? (
        <div className="payment-notice member-notice">
          <div className="notice-content">
            <div className="notice-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
                <path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" fill="#ff9800" />
              </svg>
            </div>
            <div className="notice-text">
              <p>
                <strong>View-Only Access:</strong> You currently have view-only access to this workspace.
                Please contact the workspace admin to upgrade your access.
              </p>
            </div>
          </div>
          {/* <div className="notice-actions">
            <button className="dismiss-button" onClick={handleDismiss}>
              Dismiss
            </button>
          </div> */}
        </div>
      ) : null}
    </div>
  );
});

PaymentNotice.displayName = 'PaymentNotice';

export default PaymentNotice;
